import '@cbar/cbar-localizations/register';
import './localizations/register';

import {
  formatDate,
  formatList,
  formatNumber,
  formatPrice,
  getCurrencySymbol,
  getLocale,
  getSupportedLngs,
  init,
  setLocale,
  subscribe,
  t,
} from '@cbar/cbar-i18n';

export {formatDate, formatList, formatNumber, formatPrice, getCurrencySymbol, getLocale, subscribe, t};

let setupPromise: Promise<void> | null = null;

function resolveBrowserLocale(): string {
  const supported = getSupportedLngs();
  const supportedLanguageRoots = new Set(
    supported.map((code) => code.split('-')[0]),
  );
  const candidates = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (supported.includes(candidate)) {
      return candidate;
    }

    const [language] = candidate.split('-');
    if (supported.includes(language) || supportedLanguageRoots.has(language)) {
      return candidate;
    }
  }

  return 'en-AU';
}

/** Initialise CBAR i18n using the visitor's browser locale for Intl formatting. */
export function setupI18n(): Promise<void> {
  if (!setupPromise) {
    init({
      fallbackLng: 'en-AU',
      defaultCurrency: 'AUD',
    });

    const locale = resolveBrowserLocale();
    document.documentElement.lang = locale;
    setupPromise = setLocale(locale);
  }

  return setupPromise;
}

export type MoneyValue = {
  amount: number | string;
  currency: string;
};

function coerceMoneyAmount(amount: unknown): number | null {
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    return amount;
  }

  if (typeof amount === 'string' && amount.trim() !== '') {
    const parsed = Number(amount);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
}

export function formatMoney(value: MoneyValue | null | undefined): string {
  if (!value?.currency) {
    return '';
  }

  const amount = coerceMoneyAmount(value.amount);
  if (amount === null) {
    return '';
  }

  return formatPrice(amount, {currency: value.currency});
}

/** Preset campaign amounts: locale currency symbol, no trailing zeros on whole dollars. */
export function formatCampaignPresetAmount(value: MoneyValue | null | undefined): string {
  if (!value?.currency) {
    return '';
  }

  const amount = coerceMoneyAmount(value.amount);
  if (amount === null) {
    return '';
  }

  return formatPrice(amount, {
    currency: value.currency,
    numberFormat: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
  });
}

export function formatMoneyList(values: MoneyValue[]): string {
  return formatList(
    values
      .map((value) => formatMoney(value))
      .filter((formatted) => formatted !== ''),
  );
}
